import { useQuery } from '@tanstack/react-query';
import { FIELD_GROUPS } from '@accheyrishtey/shared';
import { api } from '../../lib/api';
import {
  THIRTY_PLUS_OPTIONS,
  isThirtyPlusPreset,
  toggleThirtyPlusPreset,
} from '../../lib/dashboardFilters';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Input, Select } from '../ui/Field';
import { EXTRA_FILTERS, FILTER_BINDINGS, FILTER_PARAM_KEYS } from './filterConfig';

interface FilterOptions { maslak: string[]; professional_details: string[]; best_describes_you: string[] }

export function FilterPanel({ open, params, total, onChange, onClose }: {
  open: boolean;
  params: URLSearchParams;
  total: number;
  onChange: (params: URLSearchParams) => void;
  onClose: () => void;
}) {
  const options = useQuery({
    queryKey: ['filter-options'],
    queryFn: () => api.get<FilterOptions>('/profiles/filter-options'),
    enabled: open,
    staleTime: 60_000,
  });
  if (!open) return null;

  const update = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === '') next.delete(key); else next.set(key, value);
    next.delete('page'); onChange(next);
  };
  const toggle = (key: string, value: string, checked: boolean) => {
    const next = new URLSearchParams(params);
    const values = next.getAll(key).filter((item) => item !== value);
    next.delete(key);
    for (const item of checked ? [...values, value] : values) next.append(key, item);
    next.delete('page'); onChange(next);
  };
  const clear = () => {
    const next = new URLSearchParams(params);
    for (const key of FILTER_PARAM_KEYS) next.delete(key);
    next.delete('page'); onChange(next);
  };
  const activeForGroup = (group: string) => FILTER_BINDINGS
    .filter((binding) => binding.field.group === group)
    .reduce((count, binding) => count + binding.params.filter((key) => params.has(key)).length, 0);

  return <div className="drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="filter-drawer" role="dialog" aria-modal="true" aria-labelledby="filter-title">
      <header><div><p className="eyebrow">Refine results</p><h2 id="filter-title">Filter profiles</h2></div><button className="icon-button" onClick={onClose} aria-label="Close filters"><Icon name="close"/></button></header>
      <div className="filter-drawer__body">
        <details className="filter-group" open><summary>Quick filters</summary><div className="filter-group__body">
          {THIRTY_PLUS_OPTIONS.map((preset) => <label className="check-row" key={preset.gender}><input type="checkbox" checked={isThirtyPlusPreset(params, preset.gender)} onChange={() => onChange(toggleThirtyPlusPreset(params, preset.gender))}/>{preset.label}</label>)}
          {EXTRA_FILTERS.map((filter) => <label className="check-row" key={filter.key}><input type="checkbox" checked={params.get(filter.key) === 'true'} onChange={(event) => update(filter.key, event.target.checked ? 'true' : undefined)}/>{filter.label}</label>)}
          <div className="filter-row"><label>Submitted from<Input type="date" value={params.get('submitted_from') ?? ''} onChange={(event) => update('submitted_from', event.target.value)}/></label><label>Submitted to<Input type="date" value={params.get('submitted_to') ?? ''} onChange={(event) => update('submitted_to', event.target.value)}/></label></div>
        </div></details>
        {FIELD_GROUPS.map((group) => <details className="filter-group" key={group.key}><summary>{group.label}{activeForGroup(group.key) ? <span>{activeForGroup(group.key)}</span> : null}</summary><div className="filter-group__body">
          {FILTER_BINDINGS.filter((binding) => binding.field.group === group.key).map((binding) => {
            const [first, second] = binding.params;
            if (binding.kind === 'range') return <div className="filter-control" key={binding.field.key}><strong>{binding.label}</strong><div className="filter-row"><label>Minimum<Input type="number" value={params.get(first!) ?? ''} onChange={(event) => update(first!, event.target.value)}/></label><label>Maximum<Input type="number" value={params.get(second!) ?? ''} onChange={(event) => update(second!, event.target.value)}/></label></div></div>;
            if (binding.kind === 'multi') {
              const dynamic = options.data?.[binding.field.key as keyof FilterOptions] ?? [];
              const choices = binding.field.options ?? dynamic;
              if (choices.length > 0) return <div className="filter-control" key={binding.field.key}><strong>{binding.field.label}</strong><div className="multi-options">{choices.map((choice) => <label className="check-row" key={choice}><input type="checkbox" checked={params.getAll(first!).includes(choice)} onChange={(event) => toggle(first!, choice, event.target.checked)}/>{choice}</label>)}</div></div>;
              return <label className="filter-control" key={binding.field.key}><strong>{binding.field.label}</strong><Input value={params.get(first!) ?? ''} onChange={(event) => update(first!, event.target.value)} placeholder="Contains…"/></label>;
            }
            return <label className="filter-control" key={binding.field.key}><strong>{binding.field.label}</strong>{binding.field.input === 'select' ? <Select value={params.get(first!) ?? ''} onChange={(event) => update(first!, event.target.value)}><option value="">Any</option>{binding.field.options?.map((choice) => <option key={choice}>{choice}</option>)}</Select> : <Input value={params.get(first!) ?? ''} onChange={(event) => update(first!, event.target.value)} placeholder="Contains…"/>}</label>;
          })}
        </div></details>)}
      </div>
      <footer><Button variant="ghost" onClick={clear}>Clear all</Button><Button onClick={onClose}>Show {total} profiles</Button></footer>
    </aside>
  </div>;
}
